// src/App.js
import './i18n';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import Header from './layout/Header';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RightSidebar from './components/shared/RightSidebar';
import FasiDaChiedereModal from './components/preventivi/FasiDaChiedereModal';
import { PermessiProvider, usePermessi } from './context/PermessiContext';
import useBrand from './hooks/useBrand';

// Pagine — caricate in lazy per ridurre il bundle iniziale
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SubscriptionSelectionPage = lazy(() => import('./pages/SubscriptionSelectionPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfiloPage = lazy(() => import('./pages/ProfiloPage'));
const RuoliPage = lazy(() => import('./pages/RuoliPage'));
const PiccoloFormatoPage = lazy(() => import('./pages/PiccoloFormatoPage'));
const GrandeFormatoPage = lazy(() => import('./pages/GrandeFormatoPage'));

// Piccolo formato
const MaterialsPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/MaterialsPage'));
const TipiMaterialiPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/TipiMaterialiPage'));
const StampantiPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/StampantiPage'));
const TipiStampantiPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/TipiStampantiPage'));
const FormatiFogliPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/FormatiFogliPage'));
const SupportiPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/SupportiPage'));
const FiniturePiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/FiniturePage'));
const ParametriPiccoloFormatoPage = lazy(() => import('./pages/piccolo-formato/ParametriPage'));
const LavorazioniAccessoriePiccoloFormatoPage = lazy(() => import('./pages/lavorazioni/LavorazioniAccessoriePiccoloFormatoPage'));
const RegolePiccoloFormatoPage = lazy(() => import('./pages/regole/RegolePiccoloFormatoPage'));
const InchiostriPiccoloFormatoPage = lazy(() => import('./pages/inchiostri/InchiostriPiccoloFormatoPage'));

// Grande formato
const MaterialsGrandeFormatoPage = lazy(() => import('./pages/grande-formato/MaterialsPage'));
const TipiMaterialiGrandeFormatoPage = lazy(() => import('./pages/grande-formato/TipiMaterialiPage'));
const StampantiGrandeFormatoPage = lazy(() => import('./pages/grande-formato/StampantiPage'));
const TipiStampantiGrandeFormatoPage = lazy(() => import('./pages/grande-formato/TipiStampantiPage'));
const FormatiFogliGrandeFormatoPage = lazy(() => import('./pages/grande-formato/FormatiFogliPage'));
const SupportiGrandeFormatoPage = lazy(() => import('./pages/grande-formato/SupportiPage'));
const FinitureGrandeFormatoPage = lazy(() => import('./pages/grande-formato/FiniturePage'));
const ParametriGrandeFormatoPage = lazy(() => import('./pages/grande-formato/ParametriPage'));
const LavorazioniAccessorieGrandeFormatoPage = lazy(() => import('./pages/lavorazioni/LavorazioniAccessorieGrandeFormatoPage'));
const RegoleGrandeFormatoPage = lazy(() => import('./pages/regole/RegoleGrandeFormatoPage'));
const InchiostriGrandeFormatoPage = lazy(() => import('./pages/inchiostri/InchiostriGrandeFormatoPage'));

// Moduli business
const CatalogoPage = lazy(() => import('./pages/CatalogoPage'));
const ListiniPage = lazy(() => import('./pages/ListiniPage'));
const OrdiniPage = lazy(() => import('./pages/OrdiniPage'));
const OrdineDettagliPage = lazy(() => import('./pages/OrdineDettagliPage'));
const PreventiviPage = lazy(() => import('./pages/PreventiviPage'));
const ClientiPage = lazy(() => import('./pages/ClientiPage'));
const ClienteDetailPage = lazy(() => import('./pages/ClienteDetailPage'));
const ProduzionePage = lazy(() => import('./pages/ProduzionePage'));
const OperatoriPage = lazy(() => import('./pages/OperatoriPage'));
const OperatoreDettagliPage = lazy(() => import('./pages/OperatoreDettagliPage'));
const TaskPage = lazy(() => import('./pages/TaskPage'));
const MagazzinoPage = lazy(() => import('./pages/MagazzinoPage'));
const StatistichePage = lazy(() => import('./pages/StatistichePage'));
const FatturazioniPage = lazy(() => import('./pages/FatturazioniPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const AiSettingsPage = lazy(() => import('./pages/AiSettingsPage'));
const ConnettoriPage = lazy(() => import('./pages/ConnettoriPage'));
const EsportazionePage = lazy(() => import('./pages/EsportazionePage'));
const PersonalizzazioniPage = lazy(() => import('./pages/PersonalizzazioniPage'));
const BackupPage = lazy(() => import('./pages/BackupPage'));
const LogAttivitaPage = lazy(() => import('./pages/LogAttivitaPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const GanttPage = lazy(() => import('./pages/GanttPage'));
const CapacitaPage = lazy(() => import('./pages/CapacitaPage'));
const CrmPage = lazy(() => import('./pages/CrmPage'));
const BusinessIntelligencePage = lazy(() => import('./pages/BusinessIntelligencePage'));
const WorkflowTemplatePage = lazy(() => import('./pages/WorkflowTemplatePage'));
const OrdiniAcquistoPage = lazy(() => import('./pages/OrdiniAcquistoPage'));
const RdpPage = lazy(() => import('./pages/RdpPage'));

// Shop
const ShopProdottiPage  = lazy(() => import('./pages/ShopProdottiPage'));
const ShopCategoriePage = lazy(() => import('./pages/ShopCategoriePage'));
const ShopRichiestePage = lazy(() => import('./pages/ShopRichiestePage'));

// Preventivatore
const PreventivatorePage = lazy(() => import('./pages/PreventivatorePage'));
const ConfiguratorePreventivi = lazy(() => import('./components/preventivatore/ConfiguratorePreventivi'));
const TemplatePreventivi = lazy(() => import('./components/preventivatore/TemplatePreventivi'));
const CalcolatorePreventivi = lazy(() => import('./components/preventivatore/CalcolatorePreventivi'));
const SimulatorePreventivi = lazy(() => import('./components/preventivatore/SimulatorePreventivi'));
const CategoriePreventivi = lazy(() => import('./components/preventivatore/CategoriePreventivi'));

window.toast = toast;

const PageLoader = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Caricamento...</span>
        </div>
    </div>
);

const ProtectedRoute = ({ children, ruoli, permesso }) => {
    const token = localStorage.getItem('token');
    const location = useLocation();
    const { hasPermesso, loading } = usePermessi();

    if (!token) return <Navigate to="/login" state={{ from: location }} replace />;

    if (ruoli) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const ruolo = payload.ruolo || 'admin';
            if (!ruoli.includes(ruolo)) return <Navigate to="/dashboard" replace />;
        } catch {
            return <Navigate to="/login" replace />;
        }
    }

    if (permesso && !loading && !hasPermesso(permesso)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

const AppContent = () => {
    const location = useLocation();
    useBrand();

    const [sidebarData, setSidebarData] = React.useState({
        isOpen: false,
        title: '',
        content: null
    });

    React.useEffect(() => {
        const handleOpen = (e) => {
            setSidebarData({
                isOpen: true,
                title: e.detail.title || '',
                content: e.detail.content
            });
        };
        const handleClose = () => {
            setSidebarData({ isOpen: false, title: '', content: null });
        };

        window.addEventListener('openRightSidebar', handleOpen);
        window.addEventListener('closeRightSidebar', handleClose);
        return () => {
            window.removeEventListener('openRightSidebar', handleOpen);
            window.removeEventListener('closeRightSidebar', handleClose);
        };
    }, []);

    const publicPaths = ['/login', '/register', '/forgot-password'];
    const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/reset-password/');
    const shouldShowHeader = !isPublicPath;
    const isLoginPage = location.pathname === '/login';
    const isRegisterPage = location.pathname === '/register';

    return (
        <div className="app-layout d-flex">
            <div className="main-content flex-grow-1">
                {shouldShowHeader && <Header />}
                <div className={`page-content ${!isPublicPath ? 'p-4' : 'p-0'}`}>
                    <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/register" element={process.env.NODE_ENV !== 'production' ? <RegisterPage /> : <Navigate to="/login" replace />} />
                                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                                {process.env.NODE_ENV !== 'production' && <Route path="/subscription" element={<SubscriptionSelectionPage />} />}
                                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                                <Route path="/profilo" element={<ProtectedRoute><ProfiloPage /></ProtectedRoute>} />
                                <Route path="/negozio" element={<ProtectedRoute><ProfiloPage defaultTab="negozio" /></ProtectedRoute>} />
                                <Route path="/ruoli" element={<ProtectedRoute ruoli={['admin']}><RuoliPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato" element={<ProtectedRoute><PiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato" element={<ProtectedRoute><GrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/preventivatore" element={<ProtectedRoute permesso="preventivatore"><PreventivatorePage /></ProtectedRoute>}>
                                    <Route index element={<Navigate to="/preventivatore/configuratore" replace />} />
                                    <Route path="configuratore" element={<ConfiguratorePreventivi />} />
                                    <Route path="categorie" element={<CategoriePreventivi />} />
                                    <Route path="template" element={<TemplatePreventivi />} />
                                    <Route path="calcolatore" element={<CalcolatorePreventivi />} />
                                    <Route path="simulatore" element={<SimulatorePreventivi />} />
                                </Route>

                                <Route path="/piccolo-formato/materiali" element={<ProtectedRoute><MaterialsPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/materiali/tipi" element={<ProtectedRoute><TipiMaterialiPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/stampanti" element={<ProtectedRoute><StampantiPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/stampanti/tipi" element={<ProtectedRoute><TipiStampantiPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/formati" element={<ProtectedRoute><FormatiFogliPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/supporti" element={<ProtectedRoute><SupportiPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/finiture" element={<ProtectedRoute><FiniturePiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/parametri" element={<ProtectedRoute><ParametriPiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/lavorazioni-accessorie" element={<ProtectedRoute><LavorazioniAccessoriePiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/regole" element={<ProtectedRoute><RegolePiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/piccolo-formato/inchiostri" element={<ProtectedRoute><InchiostriPiccoloFormatoPage /></ProtectedRoute>} />

                                <Route path="/grande-formato/materiali" element={<ProtectedRoute><MaterialsGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/materiali/tipi" element={<ProtectedRoute><TipiMaterialiGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/stampanti" element={<ProtectedRoute><StampantiGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/stampanti/tipi" element={<ProtectedRoute><TipiStampantiGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/formati" element={<ProtectedRoute><FormatiFogliGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/supporti" element={<ProtectedRoute><SupportiGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/finiture" element={<ProtectedRoute><FinitureGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/parametri" element={<ProtectedRoute><ParametriGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/lavorazioni-accessorie" element={<ProtectedRoute><LavorazioniAccessorieGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/regole" element={<ProtectedRoute><RegoleGrandeFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/inchiostri" element={<ProtectedRoute><InchiostriGrandeFormatoPage /></ProtectedRoute>} />

                                <Route path="/piccolo-formato/:sezione" element={<ProtectedRoute><PiccoloFormatoPage /></ProtectedRoute>} />
                                <Route path="/grande-formato/:sezione" element={<ProtectedRoute><GrandeFormatoPage /></ProtectedRoute>} />

                                <Route path="/catalogo" element={<ProtectedRoute permesso="catalogo"><CatalogoPage /></ProtectedRoute>} />
                                <Route path="/clienti" element={<ProtectedRoute permesso="clienti"><ClientiPage /></ProtectedRoute>} />
                                <Route path="/clienti/:id" element={<ProtectedRoute permesso="clienti"><ClienteDetailPage /></ProtectedRoute>} />
                                <Route path="/preventivi" element={<ProtectedRoute permesso="preventivi"><PreventiviPage /></ProtectedRoute>} />
                                <Route path="/listini" element={<ProtectedRoute permesso="listini"><ListiniPage /></ProtectedRoute>} />
                                <Route path="/ordini" element={<ProtectedRoute permesso="ordini"><OrdiniPage /></ProtectedRoute>} />
                                <Route path="/produzione" element={<ProtectedRoute permesso="produzione"><ProduzionePage /></ProtectedRoute>} />
                                <Route path="/operatori" element={<ProtectedRoute ruoli={['admin']}><OperatoriPage /></ProtectedRoute>} />
                                <Route path="/operatori/:id" element={<ProtectedRoute ruoli={['admin']}><OperatoreDettagliPage /></ProtectedRoute>} />
                                <Route path="/task" element={<ProtectedRoute permesso="task"><TaskPage /></ProtectedRoute>} />
                                <Route path="/magazzino" element={<ProtectedRoute permesso="magazzino"><MagazzinoPage /></ProtectedRoute>} />
                                <Route path="/statistiche" element={<ProtectedRoute permesso="statistiche"><StatistichePage /></ProtectedRoute>} />
                                <Route path="/fatturazioni" element={<ProtectedRoute permesso="fatturazioni"><FatturazioniPage /></ProtectedRoute>} />

                                <Route path="/shop/prodotti"  element={<ProtectedRoute><ShopProdottiPage /></ProtectedRoute>} />
                                <Route path="/shop/categorie" element={<ProtectedRoute><ShopCategoriePage /></ProtectedRoute>} />
                                <Route path="/shop/richieste" element={<ProtectedRoute><ShopRichiestePage /></ProtectedRoute>} />

                                {process.env.NODE_ENV !== 'production' && <Route path="/abbonamento" element={<ProtectedRoute ruoli={['admin']}><SubscriptionPage /></ProtectedRoute>} />}
                                <Route path="/configurazione/ai" element={<ProtectedRoute ruoli={['admin']}><AiSettingsPage /></ProtectedRoute>} />
                                <Route path="/connettori" element={<ProtectedRoute><ConnettoriPage /></ProtectedRoute>} />
                                <Route path="/esportazione" element={<ProtectedRoute><EsportazionePage /></ProtectedRoute>} />
                                <Route path="/personalizzazioni" element={<ProtectedRoute><PersonalizzazioniPage /></ProtectedRoute>} />
                                <Route path="/backup" element={<ProtectedRoute ruoli={['admin']}><BackupPage /></ProtectedRoute>} />
                                <Route path="/log-attivita" element={<ProtectedRoute ruoli={['admin']}><LogAttivitaPage /></ProtectedRoute>} />
                                <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
                                <Route path="/gantt" element={<ProtectedRoute permesso="produzione"><GanttPage /></ProtectedRoute>} />
                                <Route path="/capacita" element={<ProtectedRoute permesso="produzione"><CapacitaPage /></ProtectedRoute>} />
                                <Route path="/crm" element={<ProtectedRoute><CrmPage /></ProtectedRoute>} />
                                <Route path="/business-intelligence" element={<ProtectedRoute permesso="statistiche"><BusinessIntelligencePage /></ProtectedRoute>} />
                                <Route path="/workflow-template" element={<ProtectedRoute permesso="produzione"><WorkflowTemplatePage /></ProtectedRoute>} />
                                <Route path="/ordini-acquisto" element={<ProtectedRoute permesso="magazzino"><OrdiniAcquistoPage /></ProtectedRoute>} />
                                <Route path="/rdp" element={<ProtectedRoute permesso="magazzino"><RdpPage /></ProtectedRoute>} />
                                <Route path="/ordini/:id" element={<OrdineDettagliPage />} />

                                <Route path="/" element={<Navigate to="/dashboard" />} />
                                <Route path="*" element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                </div>
            </div>

            <RightSidebar
                isOpen={sidebarData.isOpen}
                onClose={() => setSidebarData({ isOpen: false, title: '', content: null })}
                title={sidebarData.title}
            >
                {sidebarData.content}
            </RightSidebar>

            <FasiDaChiedereModal />

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

const App = () => (
    <Router>
        <PermessiProvider>
            <AppContent />
        </PermessiProvider>
    </Router>
);

export default App;
